import { Decimal } from 'decimal.js';
import { AnalysisMode, IndexSymbol } from '@options-oracle/types';
import type { OptionData, AnalysisResult, VarianceResult } from '@options-oracle/types';

/**
 * PURE FUNCTION: Centralized Market Calculation Logic.
 * This is the single source of truth for all complex market math.
 */
export function performMarketCalculations(
    options: OptionData[],
    spotPrice: number,
    settings: {
        mode: AnalysisMode; // 'OI' or 'VOLUME'
        range: number; // e.g., 10
        atmStrike?: number; // Optional override
        symbol: IndexSymbol;
    },
    baseOptions: OptionData[] = [],
    baseSpot: number = 0
): AnalysisResult {
    if (!options || options.length === 0) {
        return createEmptyAnalysis();
    }

    // 0. Infer Step (Strike Gap)
    const step = getStrikeStep(options, settings.symbol);

    // 1. Identify ATM Strike (Strict Formula)
    // "ATM Calculations = ROUND(Index_LTP / Strike_GAP, 0) * Strike_GAP"
    const atmStrike = settings.atmStrike !== undefined 
        ? settings.atmStrike 
        : Math.round(spotPrice / step) * step;

    // 2. Filter Active Range (ATM ± Range)
    // "data should show ATM -11 and ATM +11, ATM will be 0 means total 23 strikes"
    // Sorted by strike
    const sortedByStrike = [...options].sort((a, b) => a.strike - b.strike);
    const targetAtm = atmStrike || 0;
    
    // Find ATM Index
    let atmIndex = sortedByStrike.findIndex((o) => Math.abs(o.strike - targetAtm) < 0.1);
    if (atmIndex === -1 && sortedByStrike.length > 0) {
        const first = sortedByStrike[0]!;
        const closest = sortedByStrike.reduce((prev, curr) => {
            return Math.abs(curr.strike - targetAtm) < Math.abs(prev.strike - targetAtm) ? curr : prev;
        }, first);
        atmIndex = sortedByStrike.findIndex((o) => o.strike === closest.strike);
    }

    const range = settings.range || 10;
    const startIndex = Math.max(0, atmIndex - range);
    const endIndex = Math.min(sortedByStrike.length - 1, atmIndex + range);
    const activeStrikes = sortedByStrike.slice(startIndex, endIndex + 1);

    // Filtered set for Calculations (Exclude ATM if explicitly requested, but often needed for S/R)
    // Rule: "ATM should be ignored" for strike range calculation? 
    // Usually means ATM is the center, and we take X above and X below.
    // However, for RANK and COUNT, we usually use the filtered range.
    const activeStrikesForCalc = activeStrikes;

    // 3. Prepare Base Data
    // Decide if we use provided baseOptions or fall back to virtual base
    let baseDataForCalc: OptionData[];
    if (baseOptions && baseOptions.length > 0) {
        // Filter base options by BASE ATM ± range (not live ATM)
        // This matches DebugCountWidget.tsx which filters both live AND base
        // by their respective ATMs independently
        const baseAtm = baseSpot > 0 ? Math.round(baseSpot / step) * step : atmStrike;
        const sortedBase = [...baseOptions].sort((a, b) => a.strike - b.strike);
        let baseAtmIndex = sortedBase.findIndex(o => Math.abs(o.strike - baseAtm) < 0.1);
        if (baseAtmIndex === -1 && sortedBase.length > 0) {
            const first = sortedBase[0]!;
            const closest = sortedBase.reduce((prev, curr) =>
                Math.abs(curr.strike - baseAtm) < Math.abs(prev.strike - baseAtm) ? curr : prev, first);
            baseAtmIndex = sortedBase.findIndex(o => o.strike === closest.strike);
        }
        const bStartIndex = Math.max(0, baseAtmIndex - range);
        const bEndIndex = Math.min(sortedBase.length - 1, baseAtmIndex + range);
        baseDataForCalc = sortedBase.slice(bStartIndex, bEndIndex + 1);
    } else {
        // Fallback: Virtual Base (Day Start) = Live - Change
        baseDataForCalc = activeStrikesForCalc.map(live => {
            const ceChange = live.callOpenInterestChange || 0;
            const peChange = live.putOpenInterestChange || 0;
            return {
                ...live,
                callOpenInterest: live.callOpenInterest - ceChange,
                putOpenInterest: live.putOpenInterest - peChange,
                callVolume: settings.mode === AnalysisMode.VOLUME ? 0 : live.callVolume,
                putVolume: settings.mode === AnalysisMode.VOLUME ? 0 : live.putVolume
            };
        });
    }

    // 4. Calculate Counts (Rank Based Shift)
    const counts = calculateCountsStrict(activeStrikesForCalc, baseDataForCalc, settings.mode);

    // 5. Calculate Variance (Snapshot Delta)
    const variance = calculateVarianceStrict(activeStrikesForCalc, baseDataForCalc, settings.mode);

    // 6. Support & Resistance
    const isVolumeMode = settings.mode === AnalysisMode.VOLUME;
    
    // CE Side (Resistance)
    const top3CE = [...activeStrikesForCalc]
        .sort((a, b) => {
            const valA = isVolumeMode ? a.callVolume : a.callOpenInterest;
            const valB = isVolumeMode ? b.callVolume : b.callOpenInterest;
            return valB - valA;
        })
        .slice(0, 3);
    
    // Resistance = MIN of top 3 CE OI/Vol strikes
    const resistanceStrike = top3CE.length > 0 ? top3CE.reduce((min, s) => Math.min(min, s.strike), Infinity) : 0;
    // As per user instructions: Support Strike2 / Resistance Strike2 consider top3 Max value
    const resistanceStrike2 = top3CE.length > 0 ? top3CE.reduce((max, s) => Math.max(max, s.strike), 0) : 0;

    // PE Side (Support)
    const top3PE = [...activeStrikesForCalc]
        .sort((a, b) => {
            const valA = isVolumeMode ? a.putVolume : a.putOpenInterest;
            const valB = isVolumeMode ? b.putVolume : b.putOpenInterest;
            return valB - valA;
        })
        .slice(0, 3);

    // Support Strike = Rank 1 (highest) of top PE
    const supportStrike = top3PE.length > 0 ? top3PE.reduce((max, s) => Math.max(max, s.strike), 0) : 0;
    // As per user instructions: Support Strike2 / Resistance Strike2 consider top3 Max value
    const supportStrike2 = top3PE.length > 0 ? top3PE.reduce((max, s) => Math.max(max, s.strike), 0) : 0;

    // 7. Shift Calculation
    // Shift = (Resistance Strike + Support Strike)/2
    const shift = (resistanceStrike + supportStrike) / 2;
    const shiftOld = (resistanceStrike + supportStrike2) / 2; // Keep for legacy/context

    // 8. Global PCR
    let sumCeOI = 0, sumPeOI = 0, sumCeVol = 0, sumPeVol = 0;
    options.forEach(s => {
        sumCeOI += s.callOpenInterest || 0;
        sumPeOI += s.putOpenInterest || 0;
        sumCeVol += s.callVolume || 0;
        sumPeVol += s.putVolume || 0;
    });
    const pcrOI = sumCeOI > 0 ? sumPeOI / sumCeOI : 0;
    const pcrVolume = sumCeVol > 0 ? sumPeVol / sumCeVol : 0;

    // 9. Max Pain
    const maxPainStrike = calculateMaxPain(options);

    // Max values calculation using reduce to prevent stack overflow
    const maxCallOI = activeStrikesForCalc.length > 0 ? activeStrikesForCalc.reduce((max, s) => Math.max(max, s.callOpenInterest || 0), 0) : 0;
    const maxPutOI = activeStrikesForCalc.length > 0 ? activeStrikesForCalc.reduce((max, s) => Math.max(max, s.putOpenInterest || 0), 0) : 0;
    const maxCallVol = activeStrikesForCalc.length > 0 ? activeStrikesForCalc.reduce((max, s) => Math.max(max, s.callVolume || 0), 0) : 0;
    const maxPutVol = activeStrikesForCalc.length > 0 ? activeStrikesForCalc.reduce((max, s) => Math.max(max, s.putVolume || 0), 0) : 0;

    // 11. V1, V2, V3 (Combined Volume)
    const sortByCombinedVol = [...activeStrikesForCalc].sort((a, b) => {
        const volA = (a.callVolume || 0) + (a.putVolume || 0);
        const volB = (b.callVolume || 0) + (b.putVolume || 0);
        return volB - volA;
    });

    const v1 = sortByCombinedVol[0];
    const v2 = sortByCombinedVol[1];
    const v3 = sortByCombinedVol[2];

    const v1Strike = v1?.strike || 0;
    const v2Strike = v2?.strike || 0;
    const v3Strike = v3?.strike || 0;

    const v1Vol = v1 ? (v1.callVolume || 0) + (v1.putVolume || 0) : 0;
    const v2Vol = v2 ? (v2.callVolume || 0) + (v2.putVolume || 0) : 0;
    const v3Vol = v3 ? (v3.callVolume || 0) + (v3.putVolume || 0) : 0;

    // ShiftV OI vs Vol specific
    const topOI_CE = [...activeStrikesForCalc].sort((a,b) => (b.callOpenInterest||0) - (a.callOpenInterest||0))[0];
    const topOI_PE = [...activeStrikesForCalc].sort((a,b) => (b.putOpenInterest||0) - (a.putOpenInterest||0))[0];
    const shiftV_OI = ((topOI_CE?.strike || 0) + (topOI_PE?.strike || 0)) / 2;

    const topVol_CE = [...activeStrikesForCalc].sort((a,b) => (b.callVolume||0) - (a.callVolume||0))[0];
    const topVol_PE = [...activeStrikesForCalc].sort((a,b) => (b.putVolume||0) - (a.putVolume||0))[0];
    const shiftV_Vol = ((topVol_CE?.strike || 0) + (topVol_PE?.strike || 0)) / 2;

    return {
        supportStrike,
        resistanceStrike,
        resistanceStrike2,
        supportStrike2,
        shift,
        shiftOld,
        shiftV: (v1Strike + v2Strike) / 2,
        shiftV_OI,
        shiftV_Vol,
        pcr: pcrOI,
        pcrOI,
        pcrVolume,
        maxPainStrike,
        maxCallOI,
        maxPutOI,
        maxCallVol,
        maxPutVol,
        totalCallOI: sumCeOI,
        totalPutOI: sumPeOI,
        variance: {
            ...variance,
            ...counts
        },
        atmStrike,
        ceOtmStrike: atmStrike + step,
        peOtmStrike: atmStrike - step,
        v1Strike,
        v2Strike,
        v3Strike,
        v1Vol,
        v2Vol,
        v3Vol
    };
}

/**
 * Strict Rank Count Calculation
 * Formula: (Live Rank Strike - Base Rank Strike)
 */
export function calculateCountsStrict(
    liveOptions: OptionData[],
    baseOptions: OptionData[],
    mode: AnalysisMode
): { ceCount: number; peCount: number; totalCount: number } {
    if (!baseOptions || baseOptions.length === 0) {
        return { ceCount: 0, peCount: 0, totalCount: 0 };
    }

    const isVol = mode === AnalysisMode.VOLUME;
    const getValue = (o: OptionData, side: 'CE' | 'PE') => 
        isVol ? (side === 'CE' ? o.callVolume : o.putVolume) : (side === 'CE' ? o.callOpenInterest : o.putOpenInterest);

    // Rank Sort Descending
    const liveCe = [...liveOptions].sort((a, b) => getValue(b, 'CE') - getValue(a, 'CE'));
    const baseCe = [...baseOptions].sort((a, b) => getValue(b, 'CE') - getValue(a, 'CE'));
    
    const livePe = [...liveOptions].sort((a, b) => getValue(b, 'PE') - getValue(a, 'PE'));
    const basePe = [...baseOptions].sort((a, b) => getValue(b, 'PE') - getValue(a, 'PE'));

    let cePos = 0, ceNeg = 0;
    let pePos = 0, peNeg = 0;

    // CE Counts
    for (let i = 0; i < Math.min(liveCe.length, baseCe.length); i++) {
        const diff = liveCe[i]!.strike - baseCe[i]!.strike;
        if (diff > 0) cePos++;
        if (diff < 0) ceNeg++;
    }

    // PE Counts
    for (let i = 0; i < Math.min(livePe.length, basePe.length); i++) {
        const diff = livePe[i]!.strike - basePe[i]!.strike;
        if (diff > 0) pePos++;
        if (diff < 0) peNeg++;
    }

    return {
        ceCount: cePos - ceNeg,
        peCount: pePos - peNeg,
        totalCount: (cePos + pePos) - (ceNeg + peNeg)
    };
}

/**
 * Strict Variance Calculation
 * Formula: PE % Chng - CE % Chng
 * % Chng: (Base - Live) / Live
 * Final: IF(Min > 0, Max - Min, Max + Min)
 */
export function calculateVarianceStrict(
    liveOptions: OptionData[],
    baseOptions: OptionData[],
    mode: AnalysisMode
): Omit<VarianceResult, 'ceCount' | 'peCount' | 'totalCount'> {
    if (!baseOptions || baseOptions.length === 0) {
        return { maxDiff: 0, minDiff: 0, maxStrike: 0, minStrike: 0, finalVariance: 0 };
    }

    const baseMap = new Map<number, OptionData>();
    baseOptions.forEach(b => baseMap.set(b.strike, b));

    let maxDiff = -Infinity;
    let minDiff = Infinity;
    let maxStrike = 0;
    let minStrike = 0;

    const isVol = mode === AnalysisMode.VOLUME;
    const getValue = (o: OptionData, side: 'CE' | 'PE') => 
        isVol ? (side === 'CE' ? o.callVolume : o.putVolume) : (side === 'CE' ? o.callOpenInterest : o.putOpenInterest);

    liveOptions.forEach(live => {
        const base = baseMap.get(live.strike);
        if (!base) return;

        const liveCe = getValue(live, 'CE');
        const livePe = getValue(live, 'PE');
        const baseCe = getValue(base, 'CE');
        const basePe = getValue(base, 'PE');

        // % Change Calculation: (Base - Live) / Live (AS PER USER RULE)
        const cePct = liveCe > 0 ? (baseCe - liveCe) / liveCe : 0;
        const pePct = livePe > 0 ? (basePe - livePe) / livePe : 0;

        // Variance Calculation (AS PER USER RULE)
        // Formula = =(PE OI % Chng - CE OI % Chng)
        let diff = (pePct - cePct) * 100;

        if (diff > maxDiff) { maxDiff = diff; maxStrike = live.strike; }
        if (diff < minDiff) { minDiff = diff; minStrike = live.strike; }
    });

    if (maxDiff === -Infinity) maxDiff = 0;
    if (minDiff === Infinity) minDiff = 0;

    // Final Variance logic (AS PER USER RULE & DEBUG WIDGET)
    // IF(Min > 0, Max - Min, Max + Min)
    const finalVariance = minDiff > 0 ? (maxDiff - minDiff) : (maxDiff + minDiff);

    return {
        maxDiff,
        maxStrike,
        minDiff,
        minStrike,
        finalVariance
    };
}

/**
 * Helper: Max Pain Calculation
 */
function calculateMaxPain(options: OptionData[]): number {
    if (options.length === 0) return 0;
    const sortedOptions = [...options].sort((a, b) => a.strike - b.strike);
    
    let currentLoss = 0;
    let totalCeOiBelowOrEqual = 0;
    let totalPeOiAbove = 0;

    const firstStrike = sortedOptions[0]!.strike;
    for (const opt of sortedOptions) {
        if (opt.strike > firstStrike) {
            currentLoss += (opt.strike - firstStrike) * (opt.putOpenInterest || 0);
            totalPeOiAbove += (opt.putOpenInterest || 0);
        }
    }
    totalCeOiBelowOrEqual = (sortedOptions[0]!.callOpenInterest || 0);

    let minLoss = currentLoss;
    let maxPainStrike = firstStrike;

    for (let i = 1; i < sortedOptions.length; i++) {
        const prevStrike = sortedOptions[i - 1]!.strike;
        const currentStrike = sortedOptions[i]!.strike;
        const delta = currentStrike - prevStrike;

        currentLoss += delta * totalCeOiBelowOrEqual;
        currentLoss -= delta * totalPeOiAbove;

        if (currentLoss < minLoss) {
            minLoss = currentLoss;
            maxPainStrike = currentStrike;
        }

        totalCeOiBelowOrEqual += (sortedOptions[i]!.callOpenInterest || 0);
        totalPeOiAbove -= (sortedOptions[i]!.putOpenInterest || 0);
    }

    return maxPainStrike;
}

/**
 * Helper: Get Strike Step
 */
function getStrikeStep(options: OptionData[], symbol: IndexSymbol): number {
    if (symbol === IndexSymbol.NIFTY) return 50;
    if (symbol === IndexSymbol.SENSEX) return 100;
    
    // Auto-detect if symbol unknown
    const sorted = [...options].sort((a, b) => a.strike - b.strike);
    if (sorted.length < 2) return 50;
    return sorted[1]!.strike - sorted[0]!.strike;
}

/**
 * Helper: Create Empty Analysis
 */
function createEmptyAnalysis(): AnalysisResult {
    return {
        supportStrike: 0,
        resistanceStrike: 0,
        resistanceStrike2: 0,
        supportStrike2: 0,
        shift: 0,
        pcr: 0,
        pcrOI: 0,
        pcrVolume: 0,
        maxPainStrike: 0,
        maxCallOI: 0,
        maxPutOI: 0,
        maxCallVol: 0,
        maxPutVol: 0,
        variance: null,
        atmStrike: 0
    };
}
